import { useEffect, useState } from "react";
import { useReactivate } from "../context/ReactivateContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { X, Check, Sparkles, Zap, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ReactivatePackageModal = () => {
  const { target, closeReactivate } = useReactivate();
  const { refreshUser } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!target?.packageRef) {
      setPreview(null);
      setError(null);
      return;
    }
    let ignore = false;
    const fetchPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API}/api/billing/reactivate-preview/${encodeURIComponent(target.packageRef)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        if (ignore) return;
        if (!res.ok) throw new Error(json.detail || "Errore nel caricamento del preview");
        setPreview(json);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchPreview();
    return () => { ignore = true; };
  }, [target]);

  const handleConfirm = async () => {
    if (!preview?.package?.id) return;
    setConfirming(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/billing/reactivate-confirm`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ package_id: preview.package.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Errore nella riattivazione");
      toast.success(json.message || "Pacchetto riattivato con successo!");
      // Refresh user subscription
      if (typeof refreshUser === "function") {
        try { await refreshUser(); } catch (e) { /* noop */ }
      }
      closeReactivate();
      // Force reload to refresh sidebar feature visibility
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e.message || "Errore di rete");
    } finally {
      setConfirming(false);
    }
  };

  if (!target) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={closeReactivate}
      data-testid="reactivate-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header gradient */}
        <div
          className="relative p-6 rounded-t-3xl"
          style={{ background: "linear-gradient(135deg, #6C63FF 0%, #EC4899 100%)" }}
        >
          <button
            onClick={closeReactivate}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
            data-testid="reactivate-close-btn"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4 text-white" />
          </button>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div className="text-white">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-0.5">Riattivazione 1-click</p>
              <h2 className="text-xl font-black leading-tight">Riattiva il pacchetto</h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-8">
              <p className="text-red-600 font-semibold mb-3">{error}</p>
              <button
                onClick={closeReactivate}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold"
              >
                Chiudi
              </button>
            </div>
          )}

          {preview && !loading && !error && (
            <>
              {/* Package card */}
              <div className="rounded-2xl border-2 border-indigo-200 p-4 bg-gradient-to-br from-indigo-50/60 to-purple-50/40 mb-4" data-testid="reactivate-package-card">
                <h3 className="text-lg font-black text-slate-900 mb-1">
                  {preview.package.nome}
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {preview.package.descrizione || "Riattiva l'accesso completo a tutte le funzionalità di questo pacchetto."}
                </p>
                {preview.package.features?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {preview.package.features.slice(0, 5).map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-slate-700">
                        <Check className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                        <span className="capitalize">{f.replace(/_/g, " ")}</span>
                      </li>
                    ))}
                    {preview.package.features.length > 5 && (
                      <li className="text-[11px] text-slate-400 italic ml-5">
                        + altre {preview.package.features.length - 5} funzionalità
                      </li>
                    )}
                  </ul>
                )}
              </div>

              {/* Pricing summary */}
              <div className="space-y-2 mb-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">Totale attuale</span>
                  <span className="font-bold text-slate-700" data-testid="reactivate-current-monthly">{preview.current_monthly.toFixed(2)}€/mese</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">+ {preview.package.nome}</span>
                  <span className="font-bold text-indigo-600" data-testid="reactivate-delta">+{preview.delta_monthly.toFixed(2)}€/mese</span>
                </div>
                {preview.combo_applied && preview.saving_vs_singoli > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                    <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-[11px] font-bold text-emerald-700">
                      Combo: risparmi {preview.saving_vs_singoli.toFixed(2)}€/mese rispetto al prezzo singolo
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-slate-100">
                  <span className="font-semibold text-slate-900">Nuovo totale</span>
                  <div className="text-right">
                    <div className="text-2xl font-black text-slate-900" data-testid="reactivate-new-monthly">
                      {preview.new_monthly.toFixed(2)}€
                    </div>
                    <p className="text-[10px] text-slate-400">/mese + IVA</p>
                  </div>
                </div>
              </div>

              {/* Note */}
              <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
                L'addebito proporzionale verrà calcolato automaticamente dal prossimo ciclo di fatturazione. Puoi disdire in qualsiasi momento.
              </p>

              {/* CTA */}
              <button
                onClick={handleConfirm}
                disabled={confirming}
                className="w-full py-3 rounded-2xl text-white font-bold text-sm shadow-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(135deg, #6C63FF 0%, #EC4899 100%)" }}
                data-testid="reactivate-confirm-btn"
              >
                {confirming ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Attivazione in corso...</span>
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4" />
                    <span>Riattiva ora ({preview.delta_monthly.toFixed(2)}€/mese in più)</span>
                  </>
                )}
              </button>

              <button
                onClick={closeReactivate}
                className="w-full mt-2 py-2 text-xs text-slate-500 hover:text-slate-700 transition-colors"
              >
                Forse più tardi
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReactivatePackageModal;
